import { type Board } from '@air/api/types';

import { type BoardQueryScope } from '~/utils/mutateUtils/GalleryAssets';

export type RemovedMembership = {
  boardId: string;
  assetIds: string[];
};

export type MoveCacheRemoval = RemovedMembership & {
  boardQueryScope: BoardQueryScope;
};

const getUniqueRemovedAssetIds = (removedMemberships: RemovedMembership[]) =>
  Array.from(new Set(removedMemberships.flatMap(({ assetIds }) => assetIds)));

const isTargetInBoardTree = ({
  boardId,
  targetBoard,
}: {
  boardId: Board['id'];
  targetBoard: Pick<Board, 'id' | 'ancestors'>;
}) => targetBoard.id === boardId || targetBoard.ancestors?.some(({ id }) => id === boardId) === true;

export const getMoveCacheRemovals = ({
  includeDescendants,
  removedMemberships,
  sourceBoardId,
  targetBoard,
}: {
  includeDescendants: boolean;
  removedMemberships: RemovedMembership[];
  sourceBoardId: Board['id'];
  targetBoard: Pick<Board, 'id' | 'ancestors'>;
}): MoveCacheRemoval[] => {
  const directBoardRemovals: MoveCacheRemoval[] = removedMemberships.map((membership) => ({
    ...membership,
    boardQueryScope: 'direct',
  }));

  if (!includeDescendants) {
    return directBoardRemovals;
  }

  const assetIds = getUniqueRemovedAssetIds(removedMemberships);

  if (assetIds.length === 0) {
    return directBoardRemovals;
  }

  const removals: MoveCacheRemoval[] = removedMemberships.map(({ assetIds: directAssetIds, boardId }) =>
    isTargetInBoardTree({ boardId, targetBoard })
      ? { assetIds: directAssetIds, boardId, boardQueryScope: 'direct' }
      : { assetIds, boardId, boardQueryScope: 'both' },
  );

  const sourceRootHasRemovedMemberships = removedMemberships.some(({ boardId }) => boardId === sourceBoardId);

  if (!sourceRootHasRemovedMemberships && !isTargetInBoardTree({ boardId: sourceBoardId, targetBoard })) {
    removals.push({ assetIds, boardId: sourceBoardId, boardQueryScope: 'tree' });
  }

  return removals;
};
